import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '../types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true, // Important: send cookies with requests
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Response interceptor to handle 401 errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Don't retry if the failed request is the refresh endpoint itself
        const isRefreshEndpoint = originalRequest.url?.includes('/api/auth/refresh');

        // If 401 and not already retrying, attempt to refresh token
        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
          if (this.isRefreshing) {
            // Wait for the ongoing refresh
            return new Promise((resolve) => {
              this.refreshSubscribers.push(() => {
                resolve(this.client(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            await this.client.post('/api/auth/refresh');
            this.isRefreshing = false;
            this.onRefreshed();
            this.refreshSubscribers = [];
            return this.client(originalRequest);
          } catch (refreshError) {
            this.isRefreshing = false;
            this.refreshSubscribers = [];
            // Redirect to login page (only if not on a public page)
            const publicPaths = ['/login', '/forgot-password', '/reset-password'];
            if (!publicPaths.some(p => window.location.pathname.startsWith(p))) {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        // If refresh endpoint itself returns 401, redirect to login
        if (error.response?.status === 401 && isRefreshEndpoint) {
          const publicPaths = ['/login', '/forgot-password', '/reset-password'];
          if (!publicPaths.some(p => window.location.pathname.startsWith(p))) {
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private onRefreshed() {
    this.refreshSubscribers.forEach((callback) => callback('refreshed'));
  }

  public getClient(): AxiosInstance {
    return this.client;
  }

  // Helper method to extract error message
  public getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as ApiError;
      return apiError?.message || error.message || 'Une erreur est survenue';
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'Une erreur est survenue';
  }
}

export const apiClient = new ApiClient();
export const api = apiClient.getClient();
